import { useId, useState, type FormEvent } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DEFAULT_KEYCLOAK_CLIENT_ROLE,
  type KeycloakClientRole,
} from '../../auth/clientRoles';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { useI18n } from '../../i18n/useI18n';
import type { CreateDirectoryUserInput } from '../../types/user';
import '../lac/CreateLacUserDialog.css';
import { isValidEmailFormat, containsWhitespace } from '../../utils/formUtils';
import { dateInputToUnixMillis } from '../../utils/dateTimeUtils';
import { CreateTenantUserDialogOptionalFields } from './CreateTenantUserDialogOptionalFields';
import { CreateTenantUserDialogRequiredFields } from './CreateTenantUserDialogRequiredFields';

/** Org admins create tenant membership roles (not LAC_ADMIN). */
const TENANT_CREATABLE_ROLES: readonly KeycloakClientRole[] = [
  'ADMIN',
  'MEMBER',
];

type CreateTenantUserDialogProps = {
  onCreate: (input: CreateDirectoryUserInput) => void;
  onClose: () => void;
  isSubmitting?: boolean;
};

export function CreateTenantUserDialog({
  onCreate,
  onClose,
  isSubmitting = false,
}: CreateTenantUserDialogProps) {
  const { translate } = useI18n();
  const roleFieldId = useId();
  const sendInviteEmailFieldId = useId();
  const optionalFieldsId = useId();

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<KeycloakClientRole>(
    DEFAULT_KEYCLOAK_CLIENT_ROLE,
  );
  const [sendInviteEmail, setSendInviteEmail] = useState(true);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const trimmedEmail = email.trim();
  const isEmailInvalid =
    trimmedEmail !== '' && !isValidEmailFormat(trimmedEmail);

  const trimmedUsername = username.trim();
  const isUsernameInvalid =
    trimmedUsername !== '' && containsWhitespace(username);

  const validFromMillis = dateInputToUnixMillis(validFrom);
  const validToMillis = dateInputToUnixMillis(validTo);
  const isValidToBeforeValidFrom =
    validFromMillis != null &&
    validToMillis != null &&
    validToMillis < validFromMillis;

  const isCreateDisabled =
    isSubmitting ||
    trimmedEmail === '' ||
    isEmailInvalid ||
    trimmedUsername === '' ||
    isUsernameInvalid ||
    isValidToBeforeValidFrom ||
    firstName.trim() === '' ||
    lastName.trim() === '';

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (!usernameTouched) {
      setUsername(value.trim());
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isCreateDisabled) {
      return;
    }

    onCreate({
      username: trimmedUsername,
      email: trimmedEmail,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
      sendInviteEmail,
      validFrom: validFrom.trim() || undefined,
      validTo: validTo.trim() || undefined,
    });
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={translate('organisationManagement.users.create.title')}
      onClose={onClose}
      onSubmit={handleSubmit}
      closeLabel={translate('organisationManagement.users.create.close')}
      closeDisabled={isSubmitting}
      isDismissDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {translate('organisationManagement.users.create.cancel')}
          </LiksButton>
          <LiksButton type="submit" disabled={isCreateDisabled}>
            {translate('organisationManagement.users.create.submit')}
          </LiksButton>
        </>
      }
    >
      <CreateTenantUserDialogRequiredFields
        roleFieldId={roleFieldId}
        sendInviteEmailFieldId={sendInviteEmailFieldId}
        role={role}
        creatableRoles={TENANT_CREATABLE_ROLES}
        onRoleChange={setRole}
        email={email}
        onEmailChange={handleEmailChange}
        isEmailInvalid={isEmailInvalid}
        username={username}
        onUsernameChange={(value) => {
          setUsernameTouched(true);
          setUsername(value);
        }}
        isUsernameInvalid={isUsernameInvalid}
        firstName={firstName}
        onFirstNameChange={setFirstName}
        lastName={lastName}
        onLastNameChange={setLastName}
        sendInviteEmail={sendInviteEmail}
        onSendInviteEmailChange={setSendInviteEmail}
        isSubmitting={isSubmitting}
      />

      <button
        type="button"
        className="lac-create-user__details-toggle"
        aria-expanded={showOptionalFields}
        aria-controls={optionalFieldsId}
        onClick={() => setShowOptionalFields((prev) => !prev)}
        disabled={isSubmitting}
      >
        <ChevronDown
          className={[
            'lac-create-user__details-chevron',
            showOptionalFields && 'lac-create-user__details-chevron--expanded',
          ]
            .filter(Boolean)
            .join(' ')}
          strokeWidth={2}
          aria-hidden
        />
        <span>
          {showOptionalFields
            ? translate(
                'organisationManagement.users.create.hideOptionalFields',
              )
            : translate(
                'organisationManagement.users.create.showOptionalFields',
              )}
        </span>
      </button>

      {showOptionalFields && (
        <CreateTenantUserDialogOptionalFields
          id={optionalFieldsId}
          validFrom={validFrom}
          validTo={validTo}
          isValidToBeforeValidFrom={isValidToBeforeValidFrom}
          isSubmitting={isSubmitting}
          onValidFromChange={setValidFrom}
          onValidToChange={setValidTo}
        />
      )}
    </LiksDialog>
  );
}
